import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';

function formatMoney(v) {
  return new Intl.NumberFormat('sw-TZ').format(v || 0) + ' TZS';
}

function getMonthStatus(T) {
  return {
    paid:    { bg: 'bg-green-500',  text: 'text-white',      label: T.memberDetail.statusPaid },
    partial: { bg: 'bg-yellow-400', text: 'text-yellow-900', label: T.memberDetail.statusPartial },
    gap:     { bg: 'bg-red-500',    text: 'text-white',      label: T.memberDetail.statusGap },
    future:  { bg: 'bg-gray-100',   text: 'text-gray-400',   label: T.memberDetail.statusFuture },
  };
}

const months_sw = ['Jan','Feb','Mac','Apr','Mei','Jun','Jul','Ago','Sep','Okt','Nov','Des'];
function monthLabel(ym) {
  const [y, m] = ym.split('-');
  return `${months_sw[+m - 1]} ${y}`;
}

export default function MemberDetail() {
  const { T } = useLang();
  const MONTH_STATUS = getMonthStatus(T);
  const { id } = useParams();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [member, setMember] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusSaving, setStatusSaving] = useState(false);

  useEffect(() => {
    api.get(`/members/${id}`).then((r) => {
      setMember(r.data);
      // load monthly status using the member's group_id
      const gid = r.data?.group_id;
      if (gid) {
        api.get(`/members/${id}/monthly-status?group_id=${gid}`)
          .then(ms => setMonthly(ms.data))
          .catch(() => {});
      }
    }).finally(() => setLoading(false));
  }, [id]);

  const handleToggleStatus = async () => {
    const isInactive = member.status === 'inactive';
    const msg = isInactive
      ? `Rudisha "${member.name}" kama mwanachama aktif?`
      : `Ondoa "${member.name}" kutoka kwenye kikundi?\n\nHistoria ya michango yake itabaki.`;
    if (!confirm(msg)) return;
    setStatusSaving(true);
    try {
      await api.put(`/members/${id}`, { status: isInactive ? 'active' : 'inactive' });
      setMember(prev => ({ ...prev, status: isInactive ? 'active' : 'inactive' }));
    } catch (err) {
      alert(err.response?.data?.error || 'Imeshindwa');
    } finally {
      setStatusSaving(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-gray-500">{T.common.loading}</div>;
  if (!member) return <div className="text-center py-20 text-red-500">{T.memberDetail.notFound}</div>;

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-[30px] border border-white/70 bg-[linear-gradient(135deg,rgba(15,118,110,0.96),rgba(15,23,42,0.94))] px-5 py-6 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)] sm:px-6">
        <div className="flex items-center justify-between">
          <Link to="/members" className="inline-flex rounded-full bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-50">
            ← {T.memberDetail.back}
          </Link>
          {isAdmin && (
            <button
              onClick={handleToggleStatus}
              disabled={statusSaving}
              className={`inline-flex rounded-full px-4 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                member.status === 'inactive'
                  ? 'bg-green-500/20 text-green-100 hover:bg-green-500/30'
                  : 'bg-rose-500/20 text-rose-100 hover:bg-rose-500/30'
              }`}
            >
              {statusSaving ? '…' : member.status === 'inactive' ? '↩ Rudisha' : 'Ondoa Mwanachama'}
            </button>
          )}
        </div>
        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="truncate text-2xl font-bold sm:text-3xl">{member.name}</h1>
              {member.status === 'inactive' && (
                <span className="rounded-full bg-rose-500/20 px-3 py-0.5 text-xs font-semibold text-rose-200">Ameondolewa</span>
              )}
            </div>
            <p className="mt-2 text-sm text-emerald-50/80">{T.memberDetail.title}</p>
          </div>
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[24px] bg-white/12 text-3xl shadow-inner shadow-white/10">
            🙋
          </div>
        </div>
      </div>

      {/* Info card */}
      <div className="rounded-[26px] border border-white/75 bg-white/88 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)]">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
          <InfoTile label={T.memberDetail.phone} value={member.phone} />
          {member.email && (
            <InfoTile label={T.memberDetail.email} value={member.email} />
          )}
          <InfoTile label={T.memberDetail.role} value={member.role} capitalize />
          <div className="rounded-2xl bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{T.memberDetail.status}</p>
            <div className="mt-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              member.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
            }`}>
              {member.status === 'active' ? T.memberDetail.active : T.memberDetail.inactive}
            </span>
            </div>
          </div>
          {member.joined_at && (
            <InfoTile label={T.memberDetail.joined} value={new Date(member.joined_at).toLocaleDateString('sw-TZ')} />
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="rounded-[24px] border border-emerald-200 bg-[linear-gradient(135deg,rgba(236,253,245,0.96),rgba(209,250,229,0.92))] p-4 sm:p-5">
          <div className="text-2xl">💰</div>
          <div className="mt-1 text-xl font-bold text-green-800">{formatMoney(member.stats?.total)}</div>
          <div className="text-sm text-green-700">{T.memberDetail.totalContributed}</div>
        </div>
        <div className="rounded-[24px] border border-sky-200 bg-[linear-gradient(135deg,rgba(239,246,255,0.96),rgba(219,234,254,0.92))] p-4 sm:p-5">
          <div className="text-2xl">📋</div>
          <div className="mt-1 text-xl font-bold text-blue-800">{member.stats?.count}</div>
          <div className="text-sm text-blue-700">{T.memberDetail.countContributions}</div>
        </div>
        {member.stats?.group_transfers > 0 && (
          <div className="col-span-2 rounded-[24px] border border-red-200 bg-[linear-gradient(135deg,rgba(254,242,242,0.96),rgba(254,226,226,0.92))] p-4 sm:p-5 sm:col-span-1">
            <div className="text-2xl">🏥</div>
            <div className="mt-1 text-xl font-bold text-red-700">{formatMoney(member.stats.group_transfers)}</div>
            <div className="text-sm text-red-600">{T.memberDetail.msibaPaid} ({member.stats.gt_count}×)</div>
          </div>
        )}
        {monthly?.summary && (
          <>
            <div className={`border rounded-[24px] p-4 sm:p-5 ${monthly.summary.balance >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="text-2xl">{monthly.summary.balance >= 0 ? '✅' : '⚠️'}</div>
              <div className={`text-xl font-bold mt-1 ${monthly.summary.balance >= 0 ? 'text-green-800' : 'text-red-700'}`}>
                {monthly.summary.balance >= 0 ? `+${new Intl.NumberFormat('sw-TZ').format(monthly.summary.balance)}` : new Intl.NumberFormat('sw-TZ').format(monthly.summary.balance)} TZS
              </div>
              <div className={`text-sm ${monthly.summary.balance >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                {monthly.summary.balance >= 0 ? T.memberDetail.balance : T.memberDetail.debt}
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-[24px] p-4 sm:p-5">
              <div className="text-2xl">📅</div>
              <div className="text-xl font-bold text-amber-800 mt-1">{monthly.summary.gaps}</div>
              <div className="text-sm text-amber-700">{T.memberDetail.gapMonths}</div>
            </div>
          </>
        )}
      </div>

      {/* Monthly status grid */}
      {monthly?.months?.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-700 mb-3">{T.memberDetail.monthlyStatus}</h2>
          <div className="rounded-[26px] border border-white/75 bg-white/88 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.08)]">
            <div className="flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:overflow-visible">
              {monthly.months.map(mx => {
                const s = MONTH_STATUS[mx.status] || MONTH_STATUS.future;
                return (
                  <div key={mx.month} className="min-w-[60px] text-center"
                    title={`${monthLabel(mx.month)}\nHali: ${s.label}\nAlipoza: ${new Intl.NumberFormat('sw-TZ').format(mx.paid_this_month)} TZS\nSalio: ${new Intl.NumberFormat('sw-TZ').format(mx.balance)} TZS`}>
                    <div className={`w-14 h-12 rounded-lg flex flex-col items-center justify-center ${s.bg} ${s.text} cursor-default`}>
                      <div className="text-base leading-none">
                        {mx.status === 'paid' ? '✓'
                          : mx.status === 'gap' ? '✗'
                          : mx.status === 'partial' ? '½'
                          : '·'}
                      </div>
                      {mx.paid_this_month > 0 && (
                        <div style={{ fontSize: '8px' }} className="mt-0.5 opacity-80">
                          {new Intl.NumberFormat('sw-TZ').format(mx.paid_this_month)}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{monthLabel(mx.month).split(' ')[0]}</div>
                    <div className="text-xs text-gray-400">{mx.month.slice(0,4)}</div>
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-100">
              {Object.entries(MONTH_STATUS).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5 text-xs text-gray-500">
                  <div className={`w-3 h-3 rounded-sm ${v.bg}`} />
                  {v.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Transactions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-700 mb-3">{T.memberDetail.history}</h2>
        <div className="space-y-3">
          <div className="space-y-3 md:hidden">
            {(member.transactions || []).map((t) => (
              <div key={t.id} className="rounded-[24px] border border-white/75 bg-white/88 p-4 shadow-[0_14px_35px_rgba(15,23,42,0.08)]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-mono text-slate-400">{t.receipt || t.trans_id || '-'}</p>
                    <p className="mt-1 font-semibold text-slate-800">{t.type}</p>
                  </div>
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-bold text-emerald-700">
                    {new Intl.NumberFormat('sw-TZ').format(t.amount)}
                  </span>
                </div>
                <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Tarehe</p>
                  <p className="mt-1 text-sm text-slate-700">{new Date(t.transaction_date).toLocaleString('sw-TZ')}</p>
                </div>
              </div>
            ))}
            {!(member.transactions || []).length && (
              <div className="text-center py-8 text-gray-400">{T.memberDetail.noTransactions}</div>
            )}
          </div>

          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500 text-xs uppercase">
                  <th className="text-left px-4 py-3">Receipt</th>
                  <th className="text-left px-4 py-3">Aina</th>
                  <th className="text-right px-4 py-3">Kiasi</th>
                  <th className="text-left px-4 py-3">Tarehe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(member.transactions || []).map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 text-xs font-mono">{t.receipt || t.trans_id || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{t.type}</td>
                    <td className="px-4 py-3 text-right font-semibold text-green-700">
                      {new Intl.NumberFormat('sw-TZ').format(t.amount)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {new Date(t.transaction_date).toLocaleString('sw-TZ')}
                    </td>
                  </tr>
                ))}
                {!(member.transactions || []).length && (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-gray-400">Hakuna michango bado</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoTile({ label, value, capitalize = false }) {
  return (
    <div className="rounded-2xl bg-slate-50 px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className={`mt-1 text-sm font-medium text-slate-800 ${capitalize ? 'capitalize' : ''}`}>{value}</p>
    </div>
  );
}
